import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import { Send, Bot, User as UserIcon, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import MobileNav from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AIChat() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: language === 'en' 
        ? "Hello! I'm your AI financial advisor. Ask me anything about investment strategies, portfolio analysis, market insights, or specific recommendations. I have access to your portfolio data for personalized advice."
        : language === 'fr'
        ? "Bonjour ! Je suis votre conseiller financier IA. Posez-moi toutes vos questions sur les stratégies d'investissement, l'analyse de portfolio, les insights du marché ou des recommandations spécifiques. J'ai accès à vos données de portfolio pour des conseils personnalisés."
        : "¡Hola! Soy tu asesor financiero IA. Pregúntame sobre estrategias de inversión, análisis de portfolio, insights del mercado o recomendaciones específicas. Tengo acceso a tus datos de portfolio para consejos personalizados.",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [portfolio, setPortfolio] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortfolio(response.data);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Build context with portfolio data
      const portfolioSummary = portfolio.length > 0 
        ? `User's current portfolio: ${portfolio.map(a => `${a.symbol} (${a.quantity} shares, bought at $${a.buy_price}, current $${a.current_price}, ${a.profit_loss >= 0 ? '+' : ''}${a.profit_loss_percentage.toFixed(2)}%)`).join(', ')}`
        : 'User has no portfolio assets yet.';

      const response = await axios.post(
        `${API}/chat/message`,
        {
          message: input.trim(),
          portfolio_context: portfolioSummary,
          language: language
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        model: response.data.model,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get AI response');
      
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = language === 'en' ? [
    "Analyze my portfolio risk and suggest improvements",
    "What should I rebalance this week?",
    "Find 3 high-upside stocks with controlled risk",
    "Should I buy more NVDA at current prices?",
    "Give me 3 defensive ideas for this market",
    "Analyze Bitcoin at current price",
    "What sectors look strongest today?",
    "How can I reduce my portfolio concentration risk?",
  ] : language === 'fr' ? [
    "Analyse le risque de mon portfolio et suggère des améliorations",
    "Que devrais-je rééquilibrer cette semaine ?",
    "Trouve 3 actions à fort potentiel avec risque contrôlé",
    "Devrais-je acheter plus de NVDA aux prix actuels ?",
    "Donne-moi 3 idées défensives pour ce marché",
    "Analyse le Bitcoin au prix actuel",
    "Quels secteurs sont les plus solides aujourd'hui ?",
    "Comment réduire le risque de concentration de mon portfolio ?",
  ] : [
    "Analiza el riesgo de mi portfolio y sugiere mejoras",
    "¿Qué debería rebalancear esta semana?",
    "Encuentra 3 acciones con alto potencial y riesgo controlado",
    "¿Debería comprar más NVDA a precios actuales?",
    "Dame 3 ideas defensivas para este mercado",
    "Analiza Bitcoin al precio actual",
    "¿Qué sectores están más fuertes hoy?",
    "¿Cómo puedo reducir el riesgo de concentración de mi portfolio?",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a0a0a] to-[#0a0a0a]">
      <MobileNav currentPage="chat" />

      <div className="max-w-5xl mx-auto px-6 py-8 h-[calc(100vh-120px)] flex flex-col">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Bot className="w-10 h-10 text-orange-500" />
            AI Financial Advisor
          </h1>
          <p className="text-gray-400">Ask me anything about investments, strategies, and portfolio management</p>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 glass rounded-2xl p-6 mb-6 overflow-y-auto">
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                )}
                
                <div className={`max-w-[70%] rounded-2xl p-4 ${
                  msg.role === 'user' 
                    ? 'bg-orange-500 text-black' 
                    : 'bg-zinc-900 text-white'
                }`}>
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                  {msg.model && (
                    <div className="text-xs opacity-70 mt-2">
                      Powered by {msg.model}
                    </div>
                  )}
                </div>

                {msg.role === 'user' && (
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>
            ))}
            
            {loading && (
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div className="bg-zinc-900 rounded-2xl p-4">
                  <Loader className="w-5 h-5 text-orange-500 animate-spin" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Suggested Questions */}
        {messages.length <= 1 && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-widest">
              {language === 'en' ? 'Suggested prompts' : language === 'fr' ? 'Questions suggérées' : 'Preguntas sugeridas'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {suggestedQuestions.slice(0, 6).map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => setInput(q)}
                  className="text-left p-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-orange-500/30 rounded-xl text-sm text-gray-300 transition-all group"
                >
                  <span className="text-orange-500/60 group-hover:text-orange-500 mr-1.5 text-xs">→</span>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="glass rounded-2xl p-4">
          <div className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={language === 'en' ? "Ask me anything..." : language === 'fr' ? "Posez-moi une question..." : "Pregúntame algo..."}
              className="flex-1 bg-zinc-900 border-zinc-700 text-white"
              disabled={loading}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-6"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIChat;
